import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../../utils/supabase';
import { assignEmailToGuest } from '../../services/guestService';
import { markAccountLinkRequestNotificationsRead } from '../../services/guestAccountLinkingService';
import { partitionGuestsByNameMatch } from '../../utils/guestNameMatch';
import {
  Modal,
  ModalContent,
  ModalCloseButton,
  MobileSearchField,
  ConfirmationModal,
} from '../common';
import { Heading, Text } from '../common/Typography';
import useKeyboardAwareScrollMaxHeight from '../../hooks/useKeyboardAwareScrollMaxHeight';
import { preventInputBlurOnPointerDown } from '../../utils/mobileSearchInput';

function guestLabel(guest) {
  return `${guest.first_name || ''} ${guest.last_name || ''}`.trim() || 'Guest';
}

/**
 * Full-row tap target with trailing action label so mobile users never scroll past
 * the list to reach a separate footer button.
 */
function GuestRow({ guest, busy, onAssign }) {
  const label = guestLabel(guest);
  const games = guest.game_count ?? 0;

  const activate = () => {
    if (!busy) onAssign(guest);
  };

  return (
    <div
      role="button"
      tabIndex={busy ? -1 : 0}
      aria-disabled={busy}
      onMouseDown={preventInputBlurOnPointerDown}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      }}
      className={`flex w-full min-h-[44px] touch-manipulation items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
        busy
          ? 'cursor-default border-primary/40 bg-primary/5 opacity-70'
          : 'cursor-pointer border-border-medium bg-surface hover:bg-surface-subtle'
      }`}
    >
      <div className="min-w-0">
        <div className="truncate font-medium text-text-primary">{label}</div>
        <div className="text-xs text-text-muted">
          {games} game{games !== 1 ? 's' : ''}
        </div>
      </div>
      <span className="shrink-0 text-xs font-medium text-primary">
        {busy ? 'Assigning…' : 'Assign'}
      </span>
    </div>
  );
}

const AssignGuestEmailModal = ({
  isOpen,
  onClose,
  request,
  clubId,
  currentUser,
  onAssigned,
}) => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [pendingGuest, setPendingGuest] = useState(null);
  const [assigningGuestId, setAssigningGuestId] = useState('');
  const searchAnchorRef = useRef(null);
  const listMaxHeight = useKeyboardAwareScrollMaxHeight(searchAnchorRef, { enabled: isOpen });

  const loadGuests = useCallback(async () => {
    if (!clubId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: guestsError } = await supabase
        .from('players')
        .select(`
          id,
          first_name,
          last_name,
          players_club!inner (club_id, is_active)
        `)
        .eq('is_guest', true)
        .is('merged_at', null)
        .eq('players_club.club_id', clubId)
        .eq('players_club.is_active', true)
        .or('guest_invited_email.is.null,guest_invited_email.eq.')
        .order('first_name', { ascending: true });

      if (guestsError) throw guestsError;

      const withCounts = await Promise.all(
        (data || []).map(async (guest) => {
          const { count } = await supabase
            .from('players_session_games')
            .select('*', { count: 'exact', head: true })
            .eq('player_id', guest.id);

          return { ...guest, game_count: count || 0 };
        })
      );

      setGuests(withCounts);
    } catch (err) {
      console.error('[AssignGuestEmailModal] Failed to load guests:', err);
      setError('Failed to load guest accounts');
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setPendingGuest(null);
      setAssigningGuestId('');
      setError(null);
      loadGuests();
    }
  }, [isOpen, loadGuests]);

  const filteredGuests = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return guests;

    return guests.filter((guest) => {
      const full = `${guest.first_name || ''} ${guest.last_name || ''}`.toLowerCase();
      return full.includes(term);
    });
  }, [guests, search]);

  const { suggested, others } = useMemo(
    () =>
      partitionGuestsByNameMatch(
        filteredGuests,
        request?.user_first_name,
        request?.user_last_name
      ),
    [filteredGuests, request?.user_first_name, request?.user_last_name]
  );

  const handleAssign = async (guest) => {
    if (!guest?.id) return;

    setAssigning(true);
    setAssigningGuestId(guest.id);
    setError(null);

    try {
      await assignEmailToGuest(guest.id, request.user_email, currentUser.id, clubId);

      if (request.user_id && currentUser?.id) {
        await markAccountLinkRequestNotificationsRead(clubId, request.user_id, currentUser.id);
      }

      setPendingGuest(null);
      onAssigned?.();
      onClose();
    } catch (err) {
      console.error('[AssignGuestEmailModal] Assign failed:', err);
      setError(err.message || 'Failed to assign email');
      setPendingGuest(null);
    } finally {
      setAssigning(false);
      setAssigningGuestId('');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnBackdropClick={!assigning}>
      <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border-light px-4 py-4 sm:px-6">
          <div className="min-w-0 pr-8">
            <Heading level={3}>Assign email to guest</Heading>
            <Text className="mt-1 text-sm text-text-secondary">
              {request?.user_name} · {request?.user_email}
            </Text>
          </div>
          <ModalCloseButton onClose={onClose} className="static shrink-0" disabled={assigning} />
        </div>

        <div ref={searchAnchorRef} className="shrink-0 border-b border-border-light px-4 py-4 sm:px-6">
          <MobileSearchField
            placeholder="Search guests..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={loading || assigning}
            className="w-full rounded-md border border-border-medium bg-surface px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6"
          style={listMaxHeight != null ? { maxHeight: listMaxHeight } : undefined}
        >
          {error && (
            <div className="mb-4 rounded-lg border border-danger/30 bg-danger-light/30 px-3 py-2 text-sm text-danger-dark">
              {error}
            </div>
          )}

          {loading ? (
            <Text className="py-8 text-center text-sm text-text-muted">Loading guests...</Text>
          ) : filteredGuests.length === 0 ? (
            <Text className="py-8 text-center text-sm text-text-secondary">
              No guest accounts without an email were found.
            </Text>
          ) : (
            <div className="space-y-4">
              {suggested.length > 0 && (
                <div>
                  <Text className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Suggested
                  </Text>
                  <div className="space-y-2">
                    {suggested.map((guest) => (
                      <GuestRow
                        key={guest.id}
                        guest={guest}
                        busy={assigningGuestId === guest.id}
                        onAssign={setPendingGuest}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Text className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {suggested.length > 0 ? 'All guests' : 'Guests'}
                </Text>
                <div className="space-y-2">
                  {(suggested.length > 0 ? others : filteredGuests).map((guest) => (
                    <GuestRow
                      key={guest.id}
                      guest={guest}
                      busy={assigningGuestId === guest.id}
                      onAssign={setPendingGuest}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </ModalContent>

      <ConfirmationModal
        isOpen={Boolean(pendingGuest)}
        onClose={() => !assigning && setPendingGuest(null)}
        onConfirm={() => handleAssign(pendingGuest)}
        title="Assign email to guest"
        message={
          pendingGuest ? (
            <>
              Link <strong>{request?.user_email}</strong> to guest{' '}
              <strong>{guestLabel(pendingGuest)}</strong>
              {pendingGuest.game_count != null
                ? ` (${pendingGuest.game_count} game${pendingGuest.game_count !== 1 ? 's' : ''})`
                : ''}
              ? They will be notified to merge their account.
            </>
          ) : null
        }
        confirmText="Assign email"
        loading={assigning}
      />
    </Modal>
  );
};

export default AssignGuestEmailModal;
